import { KeyboardEvent, ReactNode, useMemo, useState } from 'react'
import { Key, useFocusManager } from 'react-aria'
import {
	Label,
	ListBox,
	ListBoxItem,
	Popover,
	Select as AriaSelect,
	SelectValue,
	SelectProps,
} from 'react-aria-components'
import { Button, ButtonProps } from './button'
import { Input } from './input'

export type Option = {
	value: string | number
	label?: ReactNode
	disabled?: boolean
	textValue?: string
}
export type Options = Array<Option>

type Props = {
	label?: ReactNode
	options: Options
	span?: ButtonProps['span']
}

const listBoxItemBase = 'cursor-pointer px-2 py-1 outline-0 rounded'

export const getTextValue = (option: Option) =>
	option.textValue ?? (typeof option.label === 'string' ? option.label : String(option.value))

export function Select<T extends object>({
	label,
	options,
	className = '',
	span,
	selectedKey: initialSelectedKey,
	defaultSelectedKey,
	onSelectionChange,
	...props
}: SelectProps<T> & Props) {
	const [selectedKey, setSelectedKey] = useState(initialSelectedKey ?? defaultSelectedKey ?? '')
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const filteredOptions = useMemo(() => {
		if (!search) return options
		return options.filter(option => getTextValue(option).includes(search))
	}, [options, search])

	const onChange = (key: string | number) => {
		setSelectedKey(key)
		onSelectionChange?.(key)
		setOpen(false)
	}

	const onOpenChange = (open: boolean) => {
		setOpen(open)
		if (!open) setSearch('')
	}

	const enableSearch = options.length >= 8

	return (
		<AriaSelect
			className={`flex flex-col items-start gap-1 ${className} [&>button]:invalid:border-red-600`}
			selectedKey={selectedKey}
			onSelectionChange={onChange}
			isOpen={open}
			onOpenChange={onOpenChange}
			{...props}
		>
			<Label className="flex font-normal leading-tight text-xs">{label}</Label>
			<Button variant="INVISIBLE" size="NONE" span={span}>
				<span className="flex gap-4 justify-between items-center w-full">
					<SelectValue></SelectValue>
					<span className="text-xs">▼</span>
				</span>
			</Button>
			<Popover className="bg-white rounded border border-gray-200 shadow-xl shadow-gray-200 p-2 overflow-auto min-w-[--trigger-width]">
				{enableSearch && (
					<SearchInput
						value={search}
						onChange={setSearch}
						onSubmit={onChange}
						setOpen={setOpen}
						options={filteredOptions}
					/>
				)}
				<ListBox
					className="flex flex-col gap-1 outline-none"
					autoFocus={!enableSearch}
					renderEmptyState={() => {
						if (search)
							return (
								<div className="text-gray-400 text-center m-3">Žádné výsledky</div>
							)
						return null
					}}
				>
					{options.map(option => {
						const isDisabled = !!search && !getTextValue(option).includes(search)
						return (
							<ListBoxItem
								isDisabled={isDisabled}
								className={({ isHovered, isFocused, isSelected, isPressed }) => {
									return `${isDisabled ? 'hidden' : ''} ${listBoxItemBase} ${isHovered || isFocused ? 'bg-blue-50' : ''} ${isSelected ? 'bg-blue-200' : ''} ${isPressed ? 'bg-blue-400' : ''}`
								}}
								textValue={getTextValue(option)}
								key={option.value}
								id={option.value}
							>
								{option.label ?? option.value}
							</ListBoxItem>
						)
					})}
				</ListBox>
			</Popover>
		</AriaSelect>
	)
}

function SearchInput({
	value,
	onChange,
	onSubmit,
	setOpen,
	options,
}: {
	value: string
	onChange: (value: string) => void
	onSubmit: (value: Key) => void
	setOpen: (open: boolean) => void
	options: Options
}) {
	const focusManager = useFocusManager()

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case 'Enter':
				e.preventDefault()
				if (options.length) {
					setOpen(false)
					onSubmit(options[0].value)
					onChange('')
				}
				break
			case 'Escape':
				if (value) {
					onChange('')
				} else {
					setOpen(false)
				}
				break
			case 'ArrowDown':
			case 'ArrowUp':
				focusManager?.focusNext()
				break
		}
	}

	return (
		<Input
			className="mb-1"
			aria-label="Hledat"
			type="search"
			value={value}
			onChange={onChange}
			onKeyDown={onKeyDown}
			autoFocus
		/>
	)
}
